import { Dialog, IconButton, LuIcon, Separator, stopPropagation, Text } from "@overmap-ai/blocks"
import { type AssetType, type Stored } from "@overmap-ai/common"
import { selectAttachmentsOfAssetType } from "@overmap-ai/core"
import { EditorContent, useEditor } from "@tiptap/react"
import { memo } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useShallowEqaulSelector } from "../../store"
import {
	AssetTypeAttachmentNode,
	BASE_EXTENSIONS,
	LINK_NODES,
	MENTION_EXTENSIONS,
	useEditorContent,
} from "../../tiptap"
import { EditorContext } from "../../tiptap/context"
import { AssetTypeFileHandler } from "../../tiptap/extensions"
import { AttachmentList } from "../AttachmentList/AttachmentList"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import type { ExtendableDialogProps } from "./typings"

interface AssetTypeDescriptionDialogProps extends ExtendableDialogProps {
	assetType: Stored<AssetType>
}

export const AssetTypeDescriptionDialog = memo<AssetTypeDescriptionDialogProps>((props) => {
	const {
		assetType,
		children,
		open,
		onOpenChange, // oxlint-disable-line unbound-method
		modal,
		defaultOpen,
		size,
		...rest
	} = props

	const assetTypeAttachments = useShallowEqaulSelector(selectAttachmentsOfAssetType(assetType.uuid))

	const editorContent = useEditorContent(assetType.description)

	const editor = useEditor(
		{
			content: editorContent,
			editable: false,
			extensions: [
				...BASE_EXTENSIONS,
				...MENTION_EXTENSIONS,
				...LINK_NODES,
				AssetTypeAttachmentNode,
				AssetTypeFileHandler.configure({
					uuid: assetType.uuid,
				}),
			],
		},
		[assetType.uuid],
	)

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange} modal={modal} defaultOpen={defaultOpen}>
			{!!children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}
			<Dialog.Overlay onClick={stopPropagation} />
			<Dialog.Content className="flex flex-col gap-2" size={size} onClick={stopPropagation} {...rest}>
				<div className="flex justify-between gap-2">
					<div className="flex items-center gap-2">
						<AssetTypeIcon assetTypeId={assetType.uuid} />
						<Dialog.Title asChild>
							<Text className="truncate">{assetType.name}</Text>
						</Dialog.Title>
					</div>

					<Dialog.Close asChild>
						<IconButton variant="ghost" accentColor="base" size={size}>
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					</Dialog.Close>
				</div>

				<Separator size="full" />

				<div className="flex w-full grow flex-col gap-4 overflow-y-auto overflow-x-hidden">
					<EditorContext value={editor}>
						<EditorContent className="w-full grow" editor={editor} />
					</EditorContext>

					<AttachmentList attachments={assetTypeAttachments} service={sdk.assetTypeAttachments} />
				</div>
			</Dialog.Content>
		</Dialog.Root>
	)
})

AssetTypeDescriptionDialog.displayName = "AssetTypeDescriptionDialog"
